import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Select, { SelectOption } from '@amzn/meridian/select';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Tooltip from '@amzn/meridian/tooltip';
import HoverZoomImage from '../HoverZoomImage';
import Alert from '@amzn/meridian/alert';
import Icon from '@amzn/meridian/icon';
import Checkbox from '@amzn/meridian/checkbox';
import Loader from '@amzn/meridian/loader';
import closeKnockoutTokens from '@amzn/meridian-tokens/base/icon/close-knockout';
import { ItemEntryInput } from '@components/agile/types/ItemEntryTypes';
import { storeSubcategory } from '@components/agile/types/AgileMacroplanogramTypes';
import useGetItemMetaData from '@store/hooks/useGetItemMetaData';
import useGetSubcategoryRecommendation from '@store/hooks/useGetSubcategoryRecommendation';
import useFeatureFlag from 'src/hooks/useFeatureFlag';
import FeatureName from '@constants/featureName';
import useTableSort from 'src/hooks/useTableSort';
import usePagination from 'src/hooks/usePagination';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import { MacroplanogramScope } from 'src/types/programConfig';
import { toItemType } from 'src/utils/itemTypeUtils';
import AiSparkleIcon from '@components/agile/aiRecommendation/AiSparkleIcon';
import AiSuggestionBadge from '@components/agile/aiRecommendation/AiSuggestionBadge';
import TablePagination from '../TablePagination';
import TableSearchBar from '../TableSearchBar';
import '../ScrollableTable.css';

import { toTitleCase, findLooseSubcategoryMatch } from '@components/agile/utils/stringUtils';

export interface ItemBulkUploadReviewTableProps {
  inputItemEntries: ItemEntryInput[];
  availableSubcategories: storeSubcategory[];
  storeId: string;
  macroplanogramId: string;
  onConfirm: (validatedEntries: ItemEntryInput[]) => void;
  onCancel: () => void;
  onDuplicatesRemoved?: (count: number) => void;
}

interface ReviewEntry extends ItemEntryInput {
  isValidSubcategory: boolean;
  isConflictingDuplicate: boolean;
  compositeKey: string;
  recommendedBy?: 'AI';
  reasonCode?: string;
  reasonDetail?: string;
}

const ItemBulkUploadReviewTable = (props: ItemBulkUploadReviewTableProps) => {
  const {
    inputItemEntries,
    availableSubcategories,
    storeId,
    macroplanogramId,
    onConfirm,
    onCancel,
    onDuplicatesRemoved,
  } = props;
  const config = useProgramConfig();
  const itemIdentifierLabel = config.labels.itemIdentifierType;
  const subcategoryLabel = config.labels.subcategory;
  const categoryLabel = config.labels.category;
  const scope: MacroplanogramScope = config.features.itemAssortment?.scope ?? 'subcategory';
  const scopeColumnLabel = scope === 'subcategory' ? subcategoryLabel : categoryLabel;
  const programItemType = toItemType(itemIdentifierLabel);
  const [reviewEntries, setReviewEntries] = useState<ReviewEntry[]>([]);
  const [allValid, setAllValid] = useState(false);
  const [showInvalidFirst, setShowInvalidFirst] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [subcategorySearchQueries, setSubcategorySearchQueries] = useState<Map<string, string>>(new Map());
  const handleTableScroll = useCallback(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, []);

  // Feature flag for AI subcategory suggestion
  const isAiFeatureEnabled = useFeatureFlag(FeatureName.AI_SUBCATEGORY_SUGGESTION);

  // AI recommendation error state for inline alert
  const [aiError, setAiError] = useState<string | null>(null);

  // Compute item IDs that need recommendations (invalid/empty subcategories)
  const itemIdsWithoutSubcategory = useMemo(
    () => reviewEntries.filter((e) => !e.isValidSubcategory).map((e) => e.itemId),
    [reviewEntries],
  );

  // AI recommendation hook — lazy query, only fires on button click
  const {
    fetchRecommendations,
    isLoading: isAiLoading,
    error: aiHookError,
    data: aiRecommendations,
    reset: resetAiRecommendations,
  } = useGetSubcategoryRecommendation({
    storeId,
    macroplanogramId,
    itemIds: itemIdsWithoutSubcategory,
  });

  // Valid subcategory names for recommendation validation
  const validSubcategoryNames = useMemo(
    () => new Set(availableSubcategories.map((sc) => sc.subcategoryName.toLowerCase())),
    [availableSubcategories],
  );

  // Apply recommendations when data arrives successfully
  useEffect(() => {
    if (aiRecommendations && aiRecommendations.length > 0) {
      setReviewEntries((prev) => {
        const recMap = new Map(aiRecommendations.map((r) => [r.itemId, r]));
        return prev.map((entry) => {
          // Skip entries that already have a valid subcategory
          if (entry.isValidSubcategory) return entry;

          const rec = recMap.get(entry.itemId);
          if (!rec || !rec.subcategoryName) return entry;

          const newSubcategoryName = rec.subcategoryName;
          // Validate that the recommended subcategory exists in the available list
          if (!validSubcategoryNames.has(newSubcategoryName.toLowerCase())) return entry;

          return {
            ...entry,
            subcategory: newSubcategoryName,
            isValidSubcategory: true,
            compositeKey: `${entry.itemId.trim()}::${newSubcategoryName.toLowerCase()}`,
            recommendedBy: 'AI' as const,
            reasonCode: rec.reasonCode,
            reasonDetail: rec.reasonDetail,
          };
        });
      });
      resetAiRecommendations();
      setAiError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiRecommendations]);

  // Handle error from the hook
  useEffect(() => {
    if (aiHookError) {
      setAiError(aiHookError);
    }
  }, [aiHookError]);

  // AI Suggest button click handler
  const handleAiSuggest = useCallback(() => {
    setAiError(null);
    fetchRecommendations();
  }, [fetchRecommendations]);

  // Determine if AI Suggest button should be disabled
  const isAiSuggestDisabled = useMemo(
    () => reviewEntries.length === 0 || reviewEntries.every((e) => e.isValidSubcategory) || isAiLoading,
    [reviewEntries, isAiLoading],
  );

  // Fetch item metadata
  const itemIds = useMemo(() => inputItemEntries.map((e) => e.itemId), [inputItemEntries]);
  const itemType = programItemType;
  const { items: metadataItems, isLoading: metadataLoading } = useGetItemMetaData(
    { itemIds, itemType, storeId },
    { enabled: itemIds.length > 0 && !!storeId },
  );

  // Build a lookup map for metadata
  const metadataMap = useMemo(() => {
    const map = new Map<string, { title?: string | null; image?: string | null }>();
    metadataItems?.forEach((item) => {
      map.set(item.itemId, { title: item.title, image: item.image });
    });
    return map;
  }, [metadataItems]);

  // Initialize review entries: auto-remove exact duplicates (same itemId+subcategory
  // for WFM, same itemId for GFC), flag conflicting duplicates (same itemId, different
  // subcategory — WFM only; GFC has a single fixed scope per upload).
  useEffect(() => {
    if (inputItemEntries.length === 0) return;

    // Deduplicate. WFM: same itemId + subcategory. GFC: same itemId (scope is fixed
    // at the macroplanogram, so the CSV's now-irrelevant subcategory column should not
    // create false-distinct rows).
    const seen = new Set<string>();
    const deduped: ItemEntryInput[] = [];
    let removedCount = 0;

    inputItemEntries.forEach((entry) => {
      const canonicalName = findLooseSubcategoryMatch(entry.subcategory, availableSubcategories);
      const sub = canonicalName || entry.subcategory.trim();
      const compositeKey =
        scope === 'subcategory' ? `${entry.itemId.trim()}::${sub.toLowerCase()}` : entry.itemId.trim();
      if (seen.has(compositeKey)) {
        removedCount += 1;
      } else {
        seen.add(compositeKey);
        deduped.push({ ...entry, subcategory: sub });
      }
    });

    if (removedCount > 0) {
      onDuplicatesRemoved?.(removedCount);
    }

    // Detect conflicting duplicates (WFM only). For GFC, scope is fixed across the
    // whole upload, so by definition there are no scope-variant duplicates — the
    // dedup above already removed any same-itemId rows.
    const conflictingIds = new Set<string>();
    if (scope === 'subcategory') {
      const itemIdSubcategories = new Map<string, Set<string>>();
      deduped.forEach((entry) => {
        const id = entry.itemId.trim();
        if (!itemIdSubcategories.has(id)) {
          itemIdSubcategories.set(id, new Set());
        }
        itemIdSubcategories.get(id)!.add(entry.subcategory.toLowerCase());
      });
      [...itemIdSubcategories.entries()].filter(([, subs]) => subs.size > 1).forEach(([id]) => conflictingIds.add(id));
    }

    const entries = deduped.map((entry) => {
      const canonicalName = findLooseSubcategoryMatch(entry.subcategory, availableSubcategories);
      const sub = canonicalName || entry.subcategory.trim();
      return {
        ...entry,
        subcategory: sub,
        // GFC has no subcategory column; rows always validate.
        isValidSubcategory: scope === 'category' || !!canonicalName,
        isConflictingDuplicate: scope === 'subcategory' && conflictingIds.has(entry.itemId.trim()),
        compositeKey: scope === 'subcategory' ? `${entry.itemId.trim()}::${sub.toLowerCase()}` : entry.itemId.trim(),
      };
    });
    setReviewEntries(entries);
  }, [inputItemEntries, availableSubcategories, onDuplicatesRemoved, scope]);

  // Recompute conflicting duplicate flags (same itemId, different subcategory).
  // GFC: scope is fixed at the macroplanogram, so rows can never conflict on scope.
  const conflictingScancodes = useMemo(() => {
    if (scope === 'category') return new Set<string>();
    const itemIdSubcategories = new Map<string, Set<string>>();
    reviewEntries.forEach((entry) => {
      const id = entry.itemId.trim();
      if (!itemIdSubcategories.has(id)) {
        itemIdSubcategories.set(id, new Set());
      }
      itemIdSubcategories.get(id)!.add(entry.subcategory.toLowerCase());
    });
    return new Set([...itemIdSubcategories.entries()].filter(([, subs]) => subs.size > 1).map(([id]) => id));
  }, [reviewEntries, scope]);

  // Auto-remove duplicates when a conflicting entry's subcategory is changed to match another.
  // GFC: scope is fixed, so any same-itemId rows that survived initial dedup are auto-removed here.
  useEffect(() => {
    if (scope === 'subcategory' && conflictingScancodes.size === 0) return;

    const seen = new Set<string>();
    const toRemove = new Set<string>();

    reviewEntries.forEach((entry) => {
      const key =
        scope === 'subcategory' ? `${entry.itemId.trim()}::${entry.subcategory.toLowerCase()}` : entry.itemId.trim();
      if (seen.has(key)) {
        toRemove.add(entry.compositeKey);
      } else {
        seen.add(key);
      }
    });

    if (toRemove.size > 0) {
      setReviewEntries((prev) => prev.filter((entry) => !toRemove.has(entry.compositeKey)));
    }
  }, [reviewEntries, conflictingScancodes, scope]);

  // Check if all entries are valid and no conflicts remain
  useEffect(() => {
    setAllValid(
      reviewEntries.length > 0 &&
        reviewEntries.every((entry) => entry.isValidSubcategory) &&
        conflictingScancodes.size === 0,
    );
  }, [reviewEntries, conflictingScancodes]);

  // Handle subcategory change
  const handleSubcategoryChange = (entryKey: string, newSubcategoryName: string) => {
    setReviewEntries((prev) =>
      prev.map((entry) =>
        entry.compositeKey === entryKey
          ? {
              ...entry,
              subcategory: newSubcategoryName,
              isValidSubcategory: true,
              compositeKey: `${entry.itemId.trim()}::${newSubcategoryName.toLowerCase()}`,
              // Clear AI metadata on manual override
              recommendedBy: undefined,
              reasonCode: undefined,
              reasonDetail: undefined,
            }
          : entry,
      ),
    );
  };

  const handleDeleteEntry = (entryKey: string) => {
    setReviewEntries((prev) => prev.filter((entry) => entry.compositeKey !== entryKey));
  };

  const handleConfirm = () => {
    const validatedEntries = reviewEntries.map(({ itemId, itemType, subcategory }) => ({
      itemId,
      itemType,
      subcategory,
    }));
    onConfirm(validatedEntries);
  };

  const invalidSubcategoryCount = reviewEntries.filter((entry) => !entry.isValidSubcategory).length;

  // Build sortable rows for useTableSort
  interface SortableReviewRow {
    itemId: string;
    title: string;
    subcategory: string;
    entry: ReviewEntry;
  }

  const sortableRows: SortableReviewRow[] = useMemo(
    () =>
      reviewEntries.map((entry) => ({
        itemId: entry.itemId,
        title: metadataMap.get(entry.itemId)?.title || 'Unknown',
        // GFC has no scope column; subcategory sort value only applies to WFM/Fresh.
        subcategory: scope === 'subcategory' ? toTitleCase(entry.subcategory) : '',
        entry,
      })),
    [reviewEntries, metadataMap, scope],
  );

  const { sortColumn, sortDirection, onSort, sortData } = useTableSort<SortableReviewRow>('');
  const sortedEntries = useMemo(() => sortData(sortableRows), [sortData, sortableRows]);

  // Apply flagged-first grouping AFTER column sort so it takes precedence
  const flaggedFirstEntries = useMemo(() => {
    if (!showInvalidFirst) {
      return sortedEntries;
    }

    const flagged = sortedEntries.filter(
      (row) => !row.entry.isValidSubcategory || conflictingScancodes.has(row.entry.itemId.trim()),
    );
    const clean = sortedEntries.filter(
      (row) => row.entry.isValidSubcategory && !conflictingScancodes.has(row.entry.itemId.trim()),
    );
    return [...flagged, ...clean];
  }, [sortedEntries, showInvalidFirst, conflictingScancodes]);

  const filteredEntries = useMemo(() => {
    if (!searchValue) return flaggedFirstEntries;
    const query = searchValue.toLowerCase();
    return flaggedFirstEntries.filter(
      (row) => row.itemId.toLowerCase().includes(query) || row.title.toLowerCase().includes(query),
    );
  }, [flaggedFirstEntries, searchValue]);

  const pagination = usePagination({ totalItems: filteredEntries.length });
  const paginatedEntries = useMemo(
    () => filteredEntries.slice(pagination.visibleIndices[0], pagination.visibleIndices[1]),
    [filteredEntries, pagination.visibleIndices],
  );

  if (metadataLoading) {
    return (
      <Column alignmentHorizontal="center" spacingInset="500">
        <Loader type="circular" size="large" />
      </Column>
    );
  }

  return (
    <Column spacing="400">
      <Row alignmentHorizontal="start" alignmentVertical="center" spacingInset="300">
        <Text type="h300">Review Uploaded Entries</Text>
        {isAiFeatureEnabled && scope === 'subcategory' && (
          <Tooltip position="top" title="AI Suggest subcategories for all items">
            <Button
              type="icon"
              size="small"
              onClick={handleAiSuggest}
              disabled={isAiSuggestDisabled}
              aria-label="AI Suggest subcategories"
            >
              {isAiLoading ? <Loader type="circular" size="small" /> : <AiSparkleIcon size={20} />}
            </Button>
          </Tooltip>
        )}
        {invalidSubcategoryCount > 0 && (
          <Text type="b300">
            {invalidSubcategoryCount} invalid {invalidSubcategoryCount === 1 ? 'entry' : 'entries'}
          </Text>
        )}
      </Row>

      {aiError && (
        <Alert type="error" size="small" onClose={() => setAiError(null)}>
          {aiError}
        </Alert>
      )}

      {invalidSubcategoryCount > 0 && (
        <Alert type="warning">
          {invalidSubcategoryCount} {invalidSubcategoryCount === 1 ? 'entry has an' : 'entries have'} invalid
          subcategory. Please select a valid subcategory from the dropdown.
        </Alert>
      )}

      {invalidSubcategoryCount > 0 && (
        <Row alignmentHorizontal="start" alignmentVertical="center" spacing="200" spacingInset="200">
          <Checkbox checked={showInvalidFirst} onChange={setShowInvalidFirst} />
          <Text type="b200">Show flagged entries first</Text>
        </Row>
      )}

      <TableSearchBar value={searchValue} onChange={setSearchValue} />
      <div className="scrollable-table-container" onScroll={handleTableScroll}>
        <Table
          headerRows={1}
          showDividers
          spacing="small"
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
        >
          <TableRow backgroundColor="secondary">
            <TableCell sortColumn="itemId">{itemIdentifierLabel}</TableCell>
            <TableCell>Image</TableCell>
            <TableCell sortColumn="title">Title</TableCell>
            {scope === 'subcategory' && <TableCell sortColumn="subcategory">{scopeColumnLabel}</TableCell>}
          </TableRow>
          {paginatedEntries.map(({ entry }) => {
            const hasInvalidSubcategory = !entry.isValidSubcategory;
            const isConflict = conflictingScancodes.has(entry.itemId.trim());
            const hasError = hasInvalidSubcategory || isConflict;
            const bgColor = isConflict ? 'error' : hasInvalidSubcategory ? 'warning' : undefined;
            const meta = metadataMap.get(entry.itemId);

            return (
              <TableRow key={entry.compositeKey} highlightOnHover backgroundColor={bgColor}>
                <TableCell>
                  <Column spacing="100">
                    <Text>{entry.itemId}</Text>
                    {isConflict && (
                      <Text type="b100" color="error">
                        Error: Conflicting subcategories for the same item.
                      </Text>
                    )}
                  </Column>
                </TableCell>
                <TableCell>
                  {meta?.image ? (
                    <HoverZoomImage src={meta.image} alt={entry.itemId} width={32} height={32} />
                  ) : (
                    <Text type="b200">No image</Text>
                  )}
                </TableCell>
                <TableCell>{meta?.title || 'Unknown'}</TableCell>
                {scope === 'subcategory' ? (
                  <TableCell>
                    <Row alignmentVertical="center" spacing="200">
                      <Select
                        value={entry.subcategory}
                        onChange={(value: string) => {
                          handleSubcategoryChange(entry.compositeKey, value);
                          setSubcategorySearchQueries((prev) => {
                            const next = new Map(prev);
                            next.delete(entry.compositeKey);
                            return next;
                          });
                        }}
                        placeholder="Select subcategory"
                        searchQuery={subcategorySearchQueries.get(entry.compositeKey) ?? ''}
                        onSearch={(query: string) =>
                          setSubcategorySearchQueries((prev) => new Map(prev).set(entry.compositeKey, query))
                        }
                        error={hasInvalidSubcategory || isConflict}
                        width={200}
                      >
                        {(() => {
                          const query = subcategorySearchQueries.get(entry.compositeKey)?.toLowerCase() ?? '';
                          const filtered = availableSubcategories.filter(
                            (sc) => !query || sc.subcategoryName.toLowerCase().includes(query),
                          );
                          if (filtered.length === 0) {
                            return (
                              <Column spacingInset="300" alignmentHorizontal="start">
                                <Text type="b200" color="secondary">
                                  No matches
                                </Text>
                              </Column>
                            );
                          }
                          return filtered.map((sc) => (
                            <SelectOption
                              key={sc.subcategoryId}
                              value={sc.subcategoryName}
                              label={sc.subcategoryName}
                            />
                          ));
                        })()}
                      </Select>
                      {entry.recommendedBy === 'AI' && entry.reasonDetail && (
                        <AiSuggestionBadge reasonDetail={entry.reasonDetail} />
                      )}
                      {hasError && (
                        <Button
                          type="icon"
                          onClick={() => handleDeleteEntry(entry.compositeKey)}
                          aria-label={`Delete entry ${entry.itemId}`}
                        >
                          <Icon tokens={closeKnockoutTokens} />
                        </Button>
                      )}
                    </Row>
                  </TableCell>
                ) : null}
              </TableRow>
            );
          })}
        </Table>
      </div>

      <TablePagination pagination={pagination} totalItems={filteredEntries.length} />

      <Row alignmentHorizontal="center" spacing="300" spacingInset="400">
        <Button onClick={onCancel} type="secondary">
          Cancel
        </Button>
        <Button onClick={handleConfirm} disabled={!allValid}>
          Get Changes
        </Button>
      </Row>
    </Column>
  );
};

export default ItemBulkUploadReviewTable;
